import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTheme } from "theme";
import { Mention, MentionsInput } from "react-mentions";
import i18n from "i18n";
import lod_ from "lodash";

/** IMPORT CSS */
import "./mentions.css";

/**
 * React component for text input with user mentions (Slack/Twitter style).
 * Uses react-mentions to provide dynamic mention suggestions and insertion.
 *
 * @component
 * @param {object} props
 * @param {string} props.text - The current text, which may contain mentions in react-mentions format.
 * @param {function} props.onChange - Callback called on every text change (react-mentions event syntax).
 * @param {function} props.setFocus - Callback called on keyUp to indicate focus (e.g., for autosave or UI state management).
 * @returns {JSX.Element} A styled, dark-mode compatible text field with mention suggestions.
 *
 * @example
 * <MentionsComponent
 *   text={text}
 *   onChange={handleChange}
 *   setFocus={setFocus}
 * />
 */
export const MentionsComponent = ({ text, onChange, setFocus }) => {
	const { isDarkMode } = useTheme();
	const [userMention, setUserMention] = useState([]);

	const { users } = useSelector(state => state.userGroup);

	useEffect(() => {
		if (!lod_.isNil(users) && lod_.isArray(users) && !lod_.isEmpty(users)) {
			let usersForMentions = users
				.filter(user => user?.active === true)
				.map(user => ({
					id: user.userID,
					display: `${user.first || ""} ${user.last || ""}`.trim() || user.userID
				}));

			if (lod_.isArray(usersForMentions) && !lod_.isEmpty(usersForMentions)) {
				const allUsersItem = {
					id: "ALL_USERS",
					display: `${i18n.t("COC.mentions.ALL_USERS")}`
				};

				usersForMentions.push(allUsersItem);

				usersForMentions.sort((a, b) => a.display.localeCompare(b.display));

				setUserMention(usersForMentions);
			} else {
				setUserMention([]);
			}
		} else {
			setUserMention([]);
		}
	}, [users]);

	return (
		<MentionsInput
			value={text}
			onChange={onChange}
			onKeyUp={() => setFocus(true)}
			className={`mentions mentions--multiLine notesMentionsInput ${isDarkMode ? "mentions--dark" : ""}`}
			placeholder={i18n.t("COC.mentions.mentionTextAreaPlaceholder")}
			a11ySuggestionsListLabel={"Suggested mentions"}
			style={{
				input: { overflow: "auto" },
				minHeight: 80,

				suggestions: {
					backgroundColor: "transparent",
					top: "auto",
					bottom: "100%",
					marginBottom: "8px"
				}
			}}
		>
			<Mention
				trigger="@"
				data={search =>
					userMention
						.filter(u => u.display.toLowerCase().includes(search.toLowerCase()))
						.slice(0, 10)
				}
				markup="@[__display__](__id__)"
				displayTransform={(id, display) => `@${display}`}
				className="mentions__mention"
			/>
		</MentionsInput>
	);
};

/**
 * Transform mentions in the text to HTML span elements with a specific class for styling
 * @param {string} text - The text containing mentions in the format @[display](user:id)
 * @param {string} userID - The current user ID to highlight their mentions
 * @returns {string} - The text with mentions transformed to HTML span elements
 */
export function transformMentionsToHtml(text = "", userID = "") {
	if (!text) return "";

	return text.replace(/@\[(.*?)\]\((.*?)\)/g, (_, display, id) => {
		if (id === "ALL_USERS") {
			// Always blue for ALL_USERS, always show translated label
			return `<span class="mention-tag mention-tag--current" data-id="ALL_USERS">@${i18n.t("COC.mentions.ALL_USERS")}</span>`;
		}
		const isCurrentUser = id === userID;
		const className = isCurrentUser
			? "mention-tag mention-tag--current"
			: "mention-tag mention-tag--other";
		return `<span class="${className}" data-id="${id}">@${display}</span>`;
	});
}
